/* eslint-disable jsx-a11y/anchor-is-valid */

import { FC, useEffect, useRef, useState } from 'react';
import 'react-datepicker/dist/react-datepicker.css';
import { Link, useHistory, useParams } from 'react-router-dom';
import { Setting, statusMessages } from '../App';
import { ProductGrid } from '../components/BoxDetail/ProductGrid';
import { useBoxDetail } from '../hooks/use-box-detail';
import base from '../styles/Base.module.css';
import { BoxDetailType } from '../types/view/BoxDetailType';
export type StatusMessage = keyof typeof statusMessages;

export const BoxDetail: FC<{ setting: Setting }> = ({ setting }) => {
  // iframeの高さ調整
  const root = useRef<HTMLDivElement>(null);
  useEffect(() => {
    setting.resize(root.current?.clientHeight ?? 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [root.current?.clientHeight]);

  const reactRouterHistory = useHistory();

  const [boxDetail, setBoxDetail] = useState<BoxDetailType>({
    parentVariantOptions: [],
    childProducts: [],
    maxQuantity: 0,
    totalQuantity: 0,
  });

  const { id, boxId } = useParams<{ id: string; boxId: string }>();
  const { box, isProp, requestUpdate } = useBoxDetail(setting, id, boxId, (childProducts, parentVariantId) => {
    setBoxDetail({
      ...boxDetail,
      childProducts,
      parentVariantId,
    });
  });

  useEffect(() => {
    if (!box || !boxDetail.parentVariantId) return;

    const variant = box.boxVariants.find((v) => {
      return v.variantId === boxDetail.parentVariantId;
    });

    setBoxDetail({
      ...boxDetail,
      parentVariantOptions: box.boxVariants.flatMap((boxVariant) => {
        return box.parentProduct.variants
          .filter((variant) => `gid://shopify/ProductVariant/${variant.id}` === boxVariant.variantId)
          .map((variant) => {
            return {
              label: variant.displayName,
              value: boxVariant.variantId,
            };
          });
      }),
      maxQuantity: Number(variant?.quantity ?? '0'),
      totalQuantity: boxDetail.childProducts.reduce((sum, b) => sum + (b.quantity ?? 0), 0),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [box, boxDetail.parentVariantId, boxDetail.childProducts]);

  const onChangeParentVariantId = (e: any) => {
    setBoxDetail({
      ...boxDetail,
      parentVariantId: e.target.value,
    });
  };

  const onClickResetChildProducts = () => {
    setBoxDetail({
      ...boxDetail,
      childProducts: boxDetail.childProducts.map((childProduct) => ({
        ...childProduct,
        quantity: 0,
      })),
    });
  };

  const onClickProductLink = (variantId: number) => {
    const product = boxDetail.childProducts.find((product) => product.variantId === variantId);
    if (!product) return;

    window.parent.postMessage(
      {
        action: 'link',
        url: product.onlineStorePreviewUrl,
      },
      '*',
    );
  };

  const onChangeQuantity = (variantId: number, quantity: number) => {
    const product = boxDetail.childProducts.find((childProduct) => childProduct.variantId === variantId);
    if (!product) return;

    setBoxDetail({
      ...boxDetail,
      childProducts: boxDetail.childProducts.map((childProduct) => {
        if (childProduct.variantId !== variantId) return childProduct;

        return {
          ...childProduct,
          quantity,
        };
      }),
    });
  };

  const onSubmit = async () => {
    if (!boxDetail.parentVariantId) return;

    await requestUpdate(boxDetail.parentVariantId, boxDetail.childProducts);
  };

  const onClickComplete = () => reactRouterHistory.push(`/subscriptions/${id}`);

  const onLoadImg = () => setting.resize(root.current?.clientHeight ?? 0);

  if (!box) return <></>;

  return (
    <>
      <div style={{}} ref={root}>
        <div className="page">
          <div>
            <header>
              <div style={{ textAlign: 'center' }}>
                <h2 className="huckleberry-subscription-detail-title">{box.title}</h2>
                <div style={{ marginTop: '1em' }} dangerouslySetInnerHTML={{ __html: box.description }}></div>
              </div>
            </header>
            <div className="formLayout" style={{ marginTop: '20px' }}>
              <section>
                <select value={boxDetail.parentVariantId} style={{ width: '100%' }} onChange={onChangeParentVariantId}>
                  {boxDetail.parentVariantOptions.map((v) => {
                    return (
                      <option key={v.value} value={v.value}>
                        {v.label}
                      </option>
                    );
                  })}
                </select>
              </section>
              <section>
                <div>
                  商品を <strong>{boxDetail.maxQuantity}個</strong> 選択してください。
                </div>
                <p style={{ marginTop: '1em' }}>
                  <a style={{ color: '#2f78f6', cursor: 'pointer' }} onClick={onClickResetChildProducts}>
                    商品の選択をすべて解除（選択中の商品： {boxDetail.totalQuantity}）
                  </a>
                </p>
              </section>
              <section>
                <ProductGrid
                  products={boxDetail.childProducts}
                  maxQuantity={boxDetail.maxQuantity}
                  onClickProductLink={onClickProductLink}
                  onChangeQuantity={onChangeQuantity}
                  onLoadImg={onLoadImg}
                />
              </section>

              <section style={{ marginTop: 20 }}>
                <button
                  disabled={!(boxDetail.maxQuantity > 0 && boxDetail.maxQuantity === boxDetail.totalQuantity && !isProp)}
                  onClick={onSubmit}
                  className="btn product-form__cart-submit button button--full-width"
                >
                  保存
                </button>
              </section>

              <section style={{ marginTop: 20 }}>
                <div className="huckleberry-subscription-product-edit back">
                  <Link to={`/subscriptions/${encodeURIComponent(id)}`}>戻る</Link>
                </div>
              </section>
            </div>
          </div>
        </div>
      </div>

      <div className="micromodal" id="modal-complete" aria-hidden="true">
        <div className="micromodal-overlay" tabIndex={-1}>
          <div role="dialog" className={base.micromodalOverlayDialog} aria-modal="true" aria-labelledby="modal-complete-title">
            <header>
              <button aria-label="Close modal" className={base.closeModal} data-micromodal-close></button>
              <h2 id="modal-complete-title">BOX商品の変更</h2>
            </header>
            <main id="modal-complete-content">
              <p>
                BOX商品の内容を変更しました。
                <br />
                次回のご注文から変更されます。
                <br />
                <br />
              </p>
            </main>
            <footer>
              <button className="btn" onClick={onClickComplete}>
                ＯＫ
              </button>
            </footer>
          </div>
        </div>
      </div>
      <div className="micromodal" id="modal-error" aria-hidden="true">
        <div className="micromodal-overlay" tabIndex={-1}>
          <div role="dialog" className={base.micromodalOverlayDialog} aria-modal="true" aria-labelledby="modal-error-title">
            <header>
              <button aria-label="Close modal" className={base.closeModal} data-micromodal-close></button>
              <h2 id="modal-error-title">エラー</h2>
            </header>
            <main id="modal-error-content">
              <p>エラーが発生しました。店舗へお問い合わせください。</p>
            </main>
            <footer>
              <button className="btn" data-micromodal-close>
                ＯＫ
              </button>
            </footer>
          </div>
        </div>
      </div>
    </>
  );
};
